import React, { type ReactElement, type ReactNode } from 'react';

import classNames from 'classnames';
import _throttle from 'lodash-es/throttle';

import VARIANTS, { type VariantType } from '../../constants/variants';

import styles from './progress_pie.module.css';

type Props = {
  size: number;
  value: number;
  variant: VariantType;
  doneElement?: ReactElement | string;
  showLabel?: boolean;
};

type State = {
  value: number;
  isDone: boolean;
};

const pieStrokeDasharray = (value: number) => {
  const radius = 25;
  const circumference = 2 * radius * Math.PI;
  const percentValue = (value / 100) * circumference;
  return `${percentValue} ${circumference}`;
};

const updateCircleToValue = (element: SVGCircleElement, value: number) => {
  element.style.strokeDasharray = pieStrokeDasharray(value);
};

export const ANIMATION_DURATION_MS = 500;
export const PERCENT_100_IDLE_DURATION_MS = 500;

// Equals to 15 FPS
const THROTTLE_TIMEOUT_MS = 67;

class ProgressPie extends React.Component<Props, State> {
  static defaultProps = {
    size: 26,
    variant: VARIANTS.SUCCESS,
    showLabel: true,
  };

  state: State = {
    value: this.props.value,
    isDone: this.props.value >= 100,
  };

  circleRef = React.createRef<SVGCircleElement>();

  intervals = new Set<number>();

  timeouts = new Set<number>();

  // Throttle function per component instance limits amount of renders up to fixed FPS.
  throttle = _throttle(
    (callback: () => void) => {
      callback();
    },
    THROTTLE_TIMEOUT_MS,
    { trailing: true },
  );

  componentDidMount(): void {
    const circleElement = this.circleRef.current;
    if (!circleElement) {
      return;
    }
    updateCircleToValue(circleElement, this.props.value);
  }

  componentWillUnmount(): void {
    this.clearTimers();
    this.throttle.cancel();
  }

  componentDidUpdate({ value: prevValue }: Readonly<Props>): void {
    const { value } = this.props;
    this.updateValue(prevValue, value);
  }

  clearTimers = (): void => {
    for (const id of this.intervals) {
      window.clearInterval(id);
    }
    for (const id of this.timeouts) {
      window.clearTimeout(id);
    }
  };

  updateValue = (fromValue: number, toValue: number): void => {
    if (fromValue === toValue) {
      return;
    }
    const diff = Math.abs(toValue - fromValue);
    const intervalTimeout = Math.round(ANIMATION_DURATION_MS / diff);
    const circleElement = this.circleRef.current;
    if (!circleElement) {
      return;
    }

    this.clearTimers();

    if (fromValue < toValue) {
      let v = fromValue;
      const intervalId = window.setInterval(() => {
        v += 1;
        this.throttle(() => {
          circleElement.style.strokeDasharray = pieStrokeDasharray(v);
          this.setState({ value: v });
        });
        if (v >= toValue) {
          window.clearInterval(intervalId);
          this.intervals.delete(intervalId);
          if (v >= 100) {
            const timeoutId = window.setTimeout(() => {
              this.setState({ isDone: true });
              this.timeouts.delete(timeoutId);
            }, PERCENT_100_IDLE_DURATION_MS);
          }
        }
      }, intervalTimeout);
      this.intervals.add(intervalId);
    }

    if (fromValue > toValue) {
      let v = fromValue;
      const intervalId = window.setInterval(() => {
        v -= 1;
        this.throttle(() => {
          circleElement.style.strokeDasharray = pieStrokeDasharray(v);
          this.setState({ value: v });
        });
        if (v <= toValue) {
          window.clearInterval(intervalId);
          this.intervals.delete(intervalId);
        }
      }, intervalTimeout);
      this.intervals.add(intervalId);
    }
  };

  render(): ReactNode {
    const { size, variant, doneElement, showLabel } = this.props;
    const { isDone, value } = this.state;

    if (isDone && doneElement !== undefined) {
      return doneElement;
    }

    return (
      <div
        className={classNames(styles.root, styles[variant] ?? styles.success)}
      >
        <div
          style={{ width: size, paddingTop: size }}
          className={styles.pie}
          aria-valuenow={value}
        >
          <svg viewBox="0 0 100 100" className={styles.circle}>
            <circle
              ref={this.circleRef}
              r={25}
              cx={50}
              cy={50}
              className={styles.value}
            />
          </svg>
        </div>
        {showLabel && <div className={styles.label}>{value}%</div>}
      </div>
    );
  }
}

export default ProgressPie;
